import Image from "next/image";
import SVGAddress from "@/lib/svg/SVGAddress";
import SVGMail from "@/lib/svg/SVGMail";
import SVGPhone from "@/lib/svg/SVGPhone";
import { SVGArrowRight } from "@/lib/svg";
import Button from "@/components/Button";
import {
  CardContainerLocation,
  CardInfo,
  ImageContainerLocation,
  Title,
  TitleLink,
  Subtitle,
  LocationBlock,
  LocationLink,
  Label,
  IconWrapper,
  Address,
  Email,
  Phone,
  LabelText,
} from "./styles";
import { useTranslation } from "@/lib/translations";
import { imageLoader } from "@/lib/sanity/client";
import AppLink, { AppLinkProps } from "@/components/AppLink";
import { useState } from "react";
import { useRouter } from "next/router";

export type LocationProps = {
  variant: "location";
  _key: string;
  title?: string;
  subtitle?: string;
  address?: string;
  email?: string;
  link?: {
    _type: string;
    url?: string;
    params?: string;
    title?: string;
    blank?: boolean;
    page?: {
      _type: string;
      slug: string;
    };
  };
  phone?: string;
  image?: {
    asset?: {
      url?: string;
      metadata?: {
        dimensions?: {
          width: number;
          height: number;
        };
      };
    };
    alt?: string;
  };
  fadeInCursorAction: any,
  fadeOutCursorAction: any,
};

const Location: React.FC<LocationProps> = (props) => {
  const { title, subtitle, email, phone, address, image, link } = props;
  const [isLoaded, setIsLoaded] = useState(false);

  const { locale = "it" } = useRouter();
  const t = {
    addressLabel: useTranslation("marketingPage.location.address", locale),
    emailLabel: useTranslation("marketingPage.location.email", locale),
    phoneLabel: useTranslation("marketingPage.location.phone", locale),
  };

  return (
    <CardContainerLocation>
      {link && link.page && (
        <AppLink page={link.page || null} params={link.params || null}>
          <TitleLink
            onMouseEnter={() => props.fadeOutCursorAction()}
            onMouseLeave={() => props.fadeInCursorAction()}
            >
            <Title as="h3">{title}</Title>
          </TitleLink>
        </AppLink>
      )}
      {link && link.url && (
        <TitleLink href={link.url || "/"} target={`${link?.blank ? "_blank" : ""}`}
          onMouseEnter={() => props.fadeOutCursorAction()}
          onMouseLeave={() => props.fadeInCursorAction()}
        >
          <Title as="h3">{title}</Title>
        </TitleLink>
      )}
      {!link && title && <Title as="h3">{title}</Title>}
      {link && link.page && link.title && (
        <LocationLink>
          <AppLink page={link.page || null} params={link.params || null}>
            <Button
              as="a"
              variant="primaryUnboxed"
              icon={<SVGArrowRight />}
              onMouseEnter={() => props.fadeOutCursorAction()}
              onMouseLeave={() => props.fadeInCursorAction()}
            >
              {link.title}
            </Button>
          </AppLink>
        </LocationLink>
      )}
      {link && link.url && link.title && (
        <LocationLink>
          <Button
            as="a"
            variant="primaryUnboxed"
            icon={<SVGArrowRight />}
            href={link.url}
            target={`${link?.blank ? "_blank" : ""}`}
            onMouseEnter={() => props.fadeOutCursorAction()}
            onMouseLeave={() => props.fadeInCursorAction()}
          >
            {link.title}
          </Button>
        </LocationLink>
      )}
      <ImageContainerLocation $isLoaded={isLoaded}>
        {image && image?.asset && image.asset?.url && (
          <Image
            sizes="(max-width: 768px) 75vw, 40vw"
            src={image.asset?.url}
            layout="fill"
            objectFit="cover"
            loader={imageLoader}
            onLoadingComplete={() => setIsLoaded(true)}
            alt={image?.alt || ""}
          />
        )}
      </ImageContainerLocation>
      <CardInfo>
        {subtitle && <Subtitle>{subtitle}</Subtitle>}
        {t.addressLabel && address && (
          <LocationBlock>
            <Label>
              <IconWrapper>
                <SVGAddress />
              </IconWrapper>
              <LabelText>{t.addressLabel}:</LabelText>
            </Label>
            <Address>{address}</Address>
          </LocationBlock>
        )}
        {t.emailLabel && email && (
          <LocationBlock>
            <Label>
              <IconWrapper>
                <SVGMail />
              </IconWrapper>
              <LabelText>{t.emailLabel}:</LabelText>
            </Label>
            <Email as="a" href={`mailto:${email}`}>
              {email}
            </Email>
          </LocationBlock>
        )}
        {t.phoneLabel && phone && (
          <LocationBlock>
            <Label>
              <IconWrapper>
                <SVGPhone />
              </IconWrapper>
              <LabelText>{t.phoneLabel}:</LabelText>
            </Label>
            <Phone>{phone}</Phone>
          </LocationBlock>
        )}
      </CardInfo>
    </CardContainerLocation>
  );
};

export default Location;
